import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Wallet, ChevronDown, Plus, Receipt, AlertCircle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import GSTRechargeModal from './GSTRechargeModal';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_LABELS = {
  legal_opinions: 'Legal Opinions',
  ec_extractor: 'EC Extraction'
};

const ModuleWalletDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchModuleWallet();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchModuleWallet = async () => {
    try {
      const token = localStorage.getItem('token');
      const walletResponse = await axios.get(`${API}/api/wallet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const walletSummary = walletResponse.data || {};
      setWallet({
        ...walletSummary,
        total_credits: walletSummary.wallet?.balance || 0,
        metadata: walletSummary.wallet?.metadata || {},
      });
    } catch (error) {
      console.error('Error fetching wallet:', error);
      // toast.error('Failed to load wallet information');
    } finally {
      setLoading(false);
    }
  };

  const totalCredits = wallet?.total_credits || 0;
  const isLowBalance = totalCredits < 10;
  const pricing = wallet?.pricing || { retail: 1, apf: 4, ec_extractor: 2 };
  const pricing_inr = wallet?.pricing_inr || { retail: 500, apf: 2000, ec_extractor: 1000 };
  const walletSettings = wallet?.wallet_settings || {};
  const equivalentValue = totalCredits * (walletSettings.price_per_credit || 0);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 rounded-lg transition-colors"
        >
          <Wallet className="w-4 h-4" />
          <span>Wallet</span>
          {isLowBalance && <AlertCircle className="w-4 h-4 text-yellow-300" />}
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
            {loading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : wallet ? (
              <>
                {/* Total Credits */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800">
                      💎 {totalCredits.toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Total Credits
                    </div>
                    <div className="mt-2 text-sm text-gray-700" data-testid="wallet-dropdown-equivalent-value">
                      Equivalent Value: ₹{equivalentValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                    {isLowBalance && (
                      <div className="mt-2 text-xs text-yellow-600 font-medium">
                        ⚠️ Low balance - Please top up
                      </div>
                    )}
                    <div className="mt-3 text-xs text-gray-500" data-testid="wallet-price-per-credit-display">
                      1 credit = ₹{walletSettings.price_per_credit?.toLocaleString('en-IN') || 0}
                    </div>
                  </div>
                </div>

                {/* Module Breakdown */}
                <div className="p-4 border-b border-gray-200">
                  <div className="text-xs font-semibold text-gray-700 mb-3">Credit Usage:</div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-blue-50 px-3 py-2 rounded">
                      <span className="text-sm font-medium text-gray-700">Retail</span>
                      <span className="text-xs text-gray-600">1 opinion = {pricing.retail} credit (₹{pricing_inr.retail?.toLocaleString('en-IN')})</span>
                    </div>
                    
                    <div className="flex justify-between items-center bg-purple-50 px-3 py-2 rounded">
                      <span className="text-sm font-medium text-gray-700">APF</span>
                      <span className="text-xs text-gray-600">1 opinion = {pricing.apf} credits (₹{pricing_inr.apf?.toLocaleString('en-IN')})</span>
                    </div>
                    
                    <div className="flex justify-between items-center bg-green-50 px-3 py-2 rounded">
                      <span className="text-sm font-medium text-gray-700">EC</span>
                      <span className="text-xs text-gray-600">1 extraction = {pricing.ec_extractor} credits (₹{pricing_inr.ec_extractor?.toLocaleString('en-IN')})</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 italic text-center">
                    System automatically deducts based on format selected
                  </p>
                </div>

                {/* Actions */}
                <div className="p-4 space-y-2">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setShowBuyModal(true);
                    }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors text-sm"
                    data-testid="buy-credits-button"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Buy Credits (GST Invoice)</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/invoices');
                    }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm"
                    data-testid="view-invoices-button"
                  >
                    <FileText className="w-4 h-4" />
                    <span>View Invoices</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/wallet/transactions');
                    }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    <Receipt className="w-4 h-4" />
                    <span>View Transactions</span>
                  </button>
                </div>

                {/* Stats */}
                {wallet.metadata && (
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="text-gray-600">Total Spent</div>
                        <div className="font-semibold">{wallet.metadata.lifetime_spent || 0} credits</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Operations</div>
                        <div className="font-semibold">{wallet.metadata.total_operations || 0}</div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-6 text-center text-red-500">
                Failed to load wallet
              </div>
            )}
          </div>
        )}
      </div>

      {/* GST Recharge Modal */}
      <GSTRechargeModal 
        open={showBuyModal} 
        onClose={() => {
          setShowBuyModal(false);
        }}
        onSuccess={() => {
          fetchModuleWallet(); // Refresh wallet after purchase
        }}
      />
    </>
  );
};

export default ModuleWalletDropdown;
